import cn from 'clsx';

import ChevronDownIcon from '@/components/Icons/ChevronDownIcon';
import RemoteIcon from '@/components/RemoteIcon/RemoteIcon';
import {Text} from '@/components/Typography';
import insightDetailTypes from '@/consts/experienceScore/insightDetailTypes';
import type {InsightTileData} from '@/services/experienceScore/insightTileService';
import modalService from '@/services/modal/modalService';

interface InsightTileProps {
    tile: InsightTileData;
}

const NUMBER_PATTERN = /(\d+(?:[.,:]\d+)*)/;

/** Numbers stand out from the text around them; a value without numbers is bold as a whole. */
const TileValue = ({value = ''}: {value?: string}) => {
    const parts = value.split(NUMBER_PATTERN);

    if (parts.length === 1) return <Text variant='bodyBold'>{value}</Text>;

    return parts.map((part, index) => {
        const isNumber = index % 2 === 1;

        return (
            <Text
                key={index}
                variant={isNumber ? 'bodyBold' : 'bodySmall'}
                className={cn(!isNumber && 'dark:text-orion-text-muted')}
            >
                {part}
            </Text>
        );
    });
};

const InsightTile = ({tile}: InsightTileProps) => {
    const {code, title, icon, value, detailType, isValueBlurred, warningIcon, isWide} = tile;

    const className = cn(
        'relative flex flex-col items-start gap-2 rounded-xl bg-light-silver p-4 text-left text-14 leading-5 text-primary dark:bg-orion-2 dark:text-romance',
        isWide && 'col-span-2'
    );

    const handleClick = () => {
        if (detailType === insightDetailTypes.DRAWER) modalService.openInfo(code);
        else modalService.openDetail(code);
    };

    const content = (
        <>
            <RemoteIcon icon={icon} className='size-8' />
            <RemoteIcon icon={warningIcon} className={cn('absolute top-4 size-8', isWide ? 'right-12' : 'right-4')} />
            {isWide && <ChevronDownIcon className='absolute top-5 right-4 -rotate-90' />}
            {/* Grows to the row height so values in a row line up at the bottom. */}
            <div className='flex flex-1 flex-col gap-2'>
                <Text as='span' variant='listText' className='leading-5'>
                    {title}
                </Text>
                {/* Blurred in retail: the placeholder value must not be read out as real data. */}
                <span aria-hidden={isValueBlurred || undefined} className={cn('mt-auto', isValueBlurred && 'blur')}>
                    <TileValue value={value} />
                </span>
            </div>
        </>
    );

    if (!detailType) return <div className={className}>{content}</div>;

    return (
        <button type='button' onClick={handleClick} className={cn(className, 'cursor-pointer')}>
            {content}
        </button>
    );
};

export default InsightTile;
